/**
 * DateRangeColumnFilter - Self-contained date range filter for column headers.
 *
 * Handles:
 * - Reading current filter values from state
 * - Detecting active presets (via config-provided function)
 * - Date changes with optional mutual exclusivity (for "no X" toggles)
 * - Preset selection and clearing
 * - Rendering the dropdown UI
 *
 * This component is configuration-driven:
 * - Different date columns provide different dateRangeConfig
 * - Columns without "no X" toggle simply omit noDueDateKey
 * - Preset logic comes from the feature's filterPresets/
 */

import { X } from 'lucide-react';
import { type ReactNode, useCallback } from 'react';

import { isISODateString } from '@/utils/dateUtils';

import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../ui/catalyst/dropdown';
import { ColumnFilterHeader } from './ColumnFilterHeader';
import { ColumnFilterTrigger } from './ColumnFilterTrigger';
import { CustomDateRange } from './CustomDateRange';
import { FilterSectionHeader } from './FilterSectionHeader';
import { PresetChips } from './PresetChips';
import { SortingSection } from './SortingSection';
import type {
  ColumnFilter as ColumnFilterConfig,
  DateRangeConfig,
  FilterValues,
} from './types';

interface DateRangeColumnFilterProps {
  /** Column identifier */
  columnId: string;
  /** Display header for the column */
  header: string | ReactNode;
  /** Filter configuration (must have dateRangeConfig) */
  filter: ColumnFilterConfig;
  /** Date range configuration */
  dateRangeConfig: DateRangeConfig;
  /** Filter change handler (for boolean filters like no_due_date) */
  onChange: (columnId: string, value: string | string[] | null) => void;
  /** Date range filter change handler */
  onDateRangeFilterChange: (
    startKey: string,
    endKey: string,
    startDate?: string,
    endDate?: string,
  ) => void;
  /** All current filter values */
  allFilterValues: FilterValues;
  /** Whether column is sortable */
  sortable?: boolean;
  /** Current sort direction */
  sortDirection?: 'asc' | 'desc' | null;
  /** Sort change handler */
  onSortChange?: (columnId: string, direction: 'asc' | 'desc') => void;
  /** Custom labels for sort options */
  sortLabels?: { asc: string; desc: string };
  /** Invert arrow direction for semantic display */
  invertArrows?: boolean;
}

export const DateRangeColumnFilter = ({
  columnId,
  header,
  filter,
  dateRangeConfig,
  onChange,
  onDateRangeFilterChange,
  allFilterValues,
  sortable = false,
  sortDirection = null,
  onSortChange,
  sortLabels,
  invertArrows = false,
}: DateRangeColumnFilterProps) => {
  const {
    startKey,
    endKey,
    noDueDateKey,
    startLabel,
    endLabel,
    getActivePreset,
    getPresetValues,
  } = dateRangeConfig;

  // =========================================================================
  // READ CURRENT FILTER VALUES
  // =========================================================================

  const currentStartDate = allFilterValues[startKey];
  const currentEndDate = allFilterValues[endKey];
  const noDueDateValue = noDueDateKey ? allFilterValues[noDueDateKey] : null;

  const startDateValue = isISODateString(currentStartDate)
    ? currentStartDate
    : undefined;
  const endDateValue = isISODateString(currentEndDate)
    ? currentEndDate
    : undefined;

  const hasCustomRange = Boolean(startDateValue || endDateValue);
  const hasNoDueDate =
    noDueDateValue === 'true' || String(noDueDateValue) === 'true';

  // Determine which preset is active (using config-provided function if available)
  const activePreset = getActivePreset
    ? getActivePreset({
        startDate: startDateValue,
        endDate: endDateValue,
        noDueDate: hasNoDueDate,
      })
    : [];
  const hasPreset = activePreset.length > 0;
  const isFiltered = hasPreset || hasCustomRange || hasNoDueDate;

  // =========================================================================
  // MUTUAL EXCLUSIVITY LOGIC
  // =========================================================================
  // Handles "no due date" ↔ date range mutual exclusivity.
  //
  // Why inline instead of useDateRangeFilter hook?
  // - This component uses immediate callbacks (onChange, onDateRangeFilterChange)
  // - useDateRangeFilter is designed for batch state (AdvancedFiltersPanel pattern)
  //
  // YAGNI note:
  // - "No X" toggles are specific to due_date (tasks can have no due date)
  // - Other date columns (created_at, last_update) always have values
  // =========================================================================

  const clearAllFilters = useCallback(() => {
    if (hasCustomRange || startDateValue || endDateValue) {
      onDateRangeFilterChange(startKey, endKey, undefined, undefined);
    }
    if (hasNoDueDate && noDueDateKey) {
      onChange(noDueDateKey, null);
    }
  }, [
    hasCustomRange,
    startDateValue,
    endDateValue,
    hasNoDueDate,
    noDueDateKey,
    onDateRangeFilterChange,
    onChange,
    startKey,
    endKey,
  ]);

  // Handler for direct date changes - applies immediately, clears no_due_date
  const handleDateChange = useCallback(
    (key: 'start' | 'end', dateValue: string | undefined) => {
      // Clear no_due_date when custom date is selected (mutual exclusivity)
      if (dateValue && hasNoDueDate && noDueDateKey) {
        onChange(noDueDateKey, null);
      }

      onDateRangeFilterChange(
        startKey,
        endKey,
        key === 'start' ? dateValue : startDateValue,
        key === 'end' ? dateValue : endDateValue,
      );
    },
    [
      hasNoDueDate,
      noDueDateKey,
      onChange,
      onDateRangeFilterChange,
      startKey,
      endKey,
      startDateValue,
      endDateValue,
    ],
  );

  // Handler for preset click - uses config-provided function for date values
  const handlePresetClick = useCallback(
    (presetValues: string[], isCurrentlyActive: boolean) => {
      const presetId = presetValues[0];

      // If clicking active preset, clear it
      if (isCurrentlyActive) {
        clearAllFilters();
        return;
      }

      // Clear existing filters first
      clearAllFilters();

      // Get date values from config-provided function
      if (!getPresetValues) {
        // No preset handler - configuration error, handle gracefully
        return;
      }

      const { startDate, endDate, noDueDate } = getPresetValues(presetId);

      // Apply preset - mutual exclusivity handled by clearAllFilters above
      if (noDueDate && noDueDateKey) {
        onChange(noDueDateKey, 'true');
      } else {
        onDateRangeFilterChange(startKey, endKey, startDate, endDate);
      }
    },
    [
      clearAllFilters,
      getPresetValues,
      noDueDateKey,
      onChange,
      onDateRangeFilterChange,
      startKey,
      endKey,
    ],
  );

  // Sort change handler
  const handleSortChange = useCallback(
    (direction: 'asc' | 'desc') => {
      onSortChange?.(columnId, direction);
    },
    [columnId, onSortChange],
  );

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <Dropdown>
      <DropdownButton as={ColumnFilterTrigger}>
        <ColumnFilterHeader
          header={header}
          filterCount={isFiltered ? 1 : 0}
          sortDirection={sortDirection}
          isFiltered={isFiltered}
          invertArrows={invertArrows}
        />
      </DropdownButton>

      <DropdownMenu
        className="min-w-72 max-w-80 block! overflow-visible!"
        anchor="bottom start"
      >
        {/* Sorting section */}
        {sortable && (
          <SortingSection
            sortDirection={sortDirection}
            sortLabels={sortLabels}
            onSortChange={handleSortChange}
            invertArrows={invertArrows}
          />
        )}

        {/* Filter section header */}
        <FilterSectionHeader showSelectAll={false} />

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          className="px-3 py-2 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Preset chips */}
          {filter.presets && filter.presets.length > 0 && (
            <>
              <PresetChips
                presets={filter.presets}
                activeValues={activePreset}
                onToggle={handlePresetClick}
              />
              <div className="border-t border-zinc-200 dark:border-zinc-700" />
            </>
          )}

          {/* Custom date range */}
          <CustomDateRange
            startDate={startDateValue}
            endDate={endDateValue}
            onStartChange={(date) => handleDateChange('start', date)}
            onEndChange={(date) => handleDateChange('end', date)}
            startLabel={startLabel}
            endLabel={endLabel}
          />
        </div>

        {/* Clear filter button */}
        {isFiltered && (
          <>
            <DropdownDivider />
            <DropdownItem
              onClick={clearAllFilters}
              className="w-full flex! flex-row justify-center"
            >
              <X className="size-4" data-slot="icon" />
              <DropdownLabel>Clear filter</DropdownLabel>
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};
